// client/src/coms/views/canvas_parts/party/PartyLobby.js
// A hosted party game's lobby: invite code and link, friend invites, players with ready state, host tools
// (settings from the mode's schema, presets, visibility, kick, hand over host, change game) and lobby chat.
import React, { useMemo, useState } from 'react';
import UserAvatar from '../../games_parts/UserAvatar';
import { InviteBox, InviteFriends, visibilityOptions } from '../../games_parts/LobbyParts';
import { partyApi, describePartyError } from './usePartySession';
import { MODE_ICONS } from './partyMeta';

const GROUP_LABELS = { game: 'Game', timers: 'Timers', prompts: 'Prompts', lobby: 'Lobby' };
const TEAM_NAMES = ['Red Rockets', 'Blue Comets', 'Green Geckos', 'Gold Foxes'];
const TEAM_COLOURS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308'];

function SettingField({ def, value, editable, onChange }) {
  const id = `party-setting-${def.key}`;
  if (def.type === 'bool') {
    return (
      <label className="party-setting bool" htmlFor={id}>
        <input id={id} type="checkbox" checked={Boolean(value)} disabled={!editable} onChange={(event) => onChange(event.target.checked)} />
        <span>{def.label}</span>
        {def.help ? <small>{def.help}</small> : null}
      </label>
    );
  }
  if (def.type === 'choice') {
    return (
      <label className="party-setting" htmlFor={id}>
        <span>{def.label}</span>
        <select id={id} value={String(value)} disabled={!editable} onChange={(event) => {
          const option = def.options.find((entry) => String(entry.value) === event.target.value);
          onChange(option ? option.value : event.target.value);
        }}
        >
          {def.options.map((option) => <option key={String(option.value)} value={String(option.value)}>{option.label}</option>)}
        </select>
        {def.help ? <small>{def.help}</small> : null}
      </label>
    );
  }
  if (def.type === 'multi') {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (item) => {
      const next = selected.includes(item) ? selected.filter((entry) => entry !== item) : [...selected.filter((entry) => item === 'random' ? false : entry !== 'random'), item];
      onChange(next.length ? next : ['random']);
    };
    return (
      <div className="party-setting multi">
        <span>{def.label}</span>
        <div className="party-chips">
          {def.options.map((option) => (
            <button key={option.value} type="button" className={`party-chip${selected.includes(option.value) ? ' active' : ''}`} disabled={!editable} aria-pressed={selected.includes(option.value)} onClick={() => toggle(option.value)}>{option.label}</button>
          ))}
        </div>
      </div>
    );
  }
  if (def.type === 'words') {
    return <WordsField def={def} value={value} editable={editable} onChange={onChange} />;
  }
  return null;
}

function WordsField({ def, value, editable, onChange }) {
  const [draft, setDraft] = useState(value || '');
  const count = String(value || '').split('\n').filter(Boolean).length;
  return (
    <div className="party-setting words">
      <span>{def.label} {count ? `(${count} saved)` : ''}</span>
      {editable ? (
        <>
          <textarea rows={3} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="rubber duck&#10;haunted toaster&#10;…" aria-label={def.label} />
          <div className="party-row">
            <button type="button" className="party-btn small ghost" onClick={() => onChange(draft)}>Save pack</button>
            <small>{def.help}</small>
          </div>
        </>
      ) : <small>{count ? `${count} custom prompts` : 'None'}</small>}
    </div>
  );
}

// `server` is the Voxemera server the Games screen is open in: "Who can join" offers its members.
export default function PartyLobby({ game, view, friends = [], server = null, modes = [], onLeave }) {
  const isHost = view.me.isHost;
  const [chat, setChat] = useState('');
  const [inviting, setInviting] = useState(false);
  const settings = view.settings;
  const schema = view.mode.settings;
  const groups = useMemo(() => {
    const out = {};
    schema.forEach((def) => {
      if (!out[def.group]) out[def.group] = [];
      out[def.group].push(def);
    });
    return out;
  }, [schema]);
  const players = view.members.filter((member) => member.role === 'player');
  const spectators = view.members.filter((member) => member.role === 'spectator');
  const notReady = players.filter((member) => !member.ready);
  const teamCount = Number(settings.teamCount) || 2;
  const startProblem = players.length < view.mode.minPlayers
    ? `Needs at least ${view.mode.minPlayers} players`
    : notReady.length ? `Waiting for ${notReady.map((member) => member.name).join(', ')} to be ready`
      : players.some((member) => !member.connected) ? 'Someone is reconnecting' : '';

  const configure = (patch) => game.act('configure', patch);
  const serverId = server && Number(server.id) > 0 ? Number(server.id) : 0;
  const lobbyServerName = server && (Number(view.serverId) === serverId || view.visibility !== 'server') ? server.name : '';
  const invite = async (userId) => {
    setInviting(true);
    const result = await partyApi(`/api/party/sessions/${encodeURIComponent(view.id)}/invite`, { method: 'POST', body: { userIds: [userId] } });
    setInviting(false);
    if (!result.ok) game.setError(describePartyError(result.data && result.data.error));
    else game.setNotice('Invite sent.');
  };
  const sendChat = (event) => {
    event.preventDefault();
    const text = chat.trim();
    if (!text) return;
    setChat('');
    game.act('chat', { text });
  };

  return (
    <div className="party-lobby" style={{ '--party-accent': view.mode.accent }} data-testid="party-lobby">
      <section className="party-lobby-hero">
        <div className="party-lobby-icon" aria-hidden="true">{MODE_ICONS[view.mode.icon] || '🎨'}</div>
        <div className="party-lobby-heading">
          <div className="party-kicker">{view.mode.genre} · {view.mode.minPlayers}–{view.mode.maxPlayers} players · ~{view.mode.minutes} min</div>
          <h2>{view.mode.name}</h2>
          <p>{view.mode.description}</p>
        </div>
        <InviteBox code={view.code} />
      </section>

      <div className="party-lobby-grid">
        <section className="party-panel">
          <div className="party-panel-title">Players {players.length}/{view.maxPlayers}</div>
          <ul className="party-member-list">
            {players.map((member) => (
              <li key={member.userId} className={`party-member${member.ready ? ' ready' : ''}${member.connected ? '' : ' offline'}`} data-testid={`member-${member.userId}`}>
                <UserAvatar userId={member.userId} name={member.name} size={34} />
                <div className="party-member-info">
                  <strong>{member.name}{member.isHost ? ' 👑' : ''}{member.userId === view.me.userId ? ' (you)' : ''}</strong>
                  <span>{member.connected ? (member.ready ? 'Ready' : 'Not ready') : 'Reconnecting…'}</span>
                </div>
                {view.mode.teams ? (
                  <select
                    className="party-team-select"
                    value={member.team || 0}
                    aria-label={`Team for ${member.name}`}
                    disabled={!(isHost || (settings.teamPick === 'manual' && member.userId === view.me.userId))}
                    onChange={(event) => game.act('team', { team: Number(event.target.value), ...(member.userId === view.me.userId ? {} : { userId: member.userId }) })}
                    style={member.team ? { borderColor: TEAM_COLOURS[member.team - 1] } : undefined}
                  >
                    <option value={0}>Auto</option>
                    {TEAM_NAMES.slice(0, teamCount).map((name, k) => <option key={name} value={k + 1}>{name}</option>)}
                  </select>
                ) : null}
                {isHost && member.userId !== view.me.userId ? (
                  <span className="party-member-actions">
                    <button type="button" className="party-icon-btn" title="Make host" aria-label={`Make ${member.name} host`} onClick={() => game.act('transfer', { userId: member.userId })}>👑</button>
                    <button type="button" className="party-icon-btn danger" title="Remove from game" aria-label={`Remove ${member.name}`} onClick={() => game.act('kick', { userId: member.userId })}>✕</button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {spectators.length ? (
            <>
              <div className="party-panel-title small">Spectators</div>
              <ul className="party-member-list compact">
                {spectators.map((member) => (
                  <li key={member.userId} className="party-member">
                    <UserAvatar userId={member.userId} name={member.name} size={24} />
                    <div className="party-member-info"><strong>{member.name}</strong></div>
                    {isHost ? <button type="button" className="party-icon-btn danger" aria-label={`Remove ${member.name}`} onClick={() => game.act('kick', { userId: member.userId })}>✕</button> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <div className="party-row wrap">
            {!isHost && view.me.role === 'player' ? (
              <button type="button" className={`party-btn${view.me.ready ? ' ghost' : ''}`} onClick={() => game.act('ready', { ready: !view.me.ready })} data-testid="ready-toggle">
                {view.me.ready ? 'Not ready' : "I'm ready!"}
              </button>
            ) : null}
            {!isHost && view.me.role === 'player' && settings.allowSpectators ? <button type="button" className="party-btn ghost small" onClick={() => game.act('role', { role: 'spectator' })}>Watch instead</button> : null}
            {view.me.role === 'spectator' ? <button type="button" className="party-btn small" onClick={() => game.act('role', { role: 'player' })}>Join as a player</button> : null}
          </div>
          <InviteFriends friends={friends} memberIds={view.members.map((member) => member.userId)} busy={inviting} onInvite={invite} />
        </section>

        <section className="party-panel settings">
          <div className="party-panel-title">Settings {isHost ? '' : <small>(the host chooses)</small>}</div>
          {isHost && modes.length ? (
            <label className="party-setting">
              <span>Game</span>
              <select value={view.mode.id} onChange={(event) => configure({ modeId: event.target.value })} aria-label="Change game">
                {modes.map((mode) => <option key={mode.id} value={mode.id}>{mode.name}</option>)}
              </select>
            </label>
          ) : null}
          {view.mode.presets.length ? (
            <div className="party-setting multi">
              <span>Presets</span>
              <div className="party-chips">
                {view.mode.presets.map((preset) => (
                  <button key={preset.id} type="button" className={`party-chip${view.preset === preset.id ? ' active' : ''}`} title={preset.description} disabled={!isHost} onClick={() => configure({ preset: preset.id })}>{preset.label}</button>
                ))}
              </div>
            </div>
          ) : null}
          <label className="party-setting">
            <span>Who can join</span>
            <select value={view.visibility} disabled={!isHost} onChange={(event) => configure({ visibility: event.target.value, ...(event.target.value === 'server' && serverId ? { serverId } : {}) })} aria-label="Who can join">
              {visibilityOptions({ serverName: lobbyServerName, canvas: Boolean(view.canvasId), current: view.visibility }).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          {['game', 'timers', 'prompts', 'lobby'].filter((key) => groups[key]).map((key) => (
            <fieldset key={key} className="party-setting-group">
              <legend>{GROUP_LABELS[key]}</legend>
              {groups[key].map((def) => (
                <SettingField key={def.key} def={def} value={settings[def.key]} editable={isHost} onChange={(value) => configure({ settings: { [def.key]: value } })} />
              ))}
            </fieldset>
          ))}
          <details className="party-rules">
            <summary>How to play</summary>
            <ul>{view.mode.rules.map((rule) => <li key={rule}>{rule}</li>)}</ul>
          </details>
        </section>

        <section className="party-panel chat">
          <div className="party-panel-title">Lobby chat</div>
          <div className="party-feed" aria-live="polite">
            {view.feed.map((entry) => (
              <div key={entry.id} className={`party-feed-entry ${entry.kind}`}>{entry.kind === 'chat' ? <><strong>{entry.name}:</strong> {entry.text}</> : entry.text}</div>
            ))}
          </div>
          <form className="party-guess-box" onSubmit={sendChat}>
            <input value={chat} onChange={(event) => setChat(event.target.value)} maxLength={160} placeholder="Say hi…" aria-label="Chat message" />
            <button type="submit" className="party-btn small" disabled={!chat.trim()}>Send</button>
          </form>
          <div className="party-voice-hint">Tip: hop into a Voxemera voice channel to talk while you play.</div>
        </section>
      </div>

      <footer className="party-lobby-footer">
        <button type="button" className="party-btn ghost" onClick={onLeave}>Leave</button>
        {isHost ? (
          <div className="party-start">
            {startProblem ? <span className="party-start-problem">{startProblem}</span> : null}
            <button type="button" className="party-btn big" disabled={Boolean(startProblem)} onClick={() => game.act('start')} data-testid="party-start">Start game</button>
          </div>
        ) : <span className="party-start-problem">The host starts the game when everyone is ready.</span>}
      </footer>
    </div>
  );
}
